import { useRef, useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FaCheckCircle, FaExclamationTriangle } from 'react-icons/fa';
import { useParams, useNavigate } from 'react-router-dom';

import PageHeader from '../components/PageHeader';
import TabNav from '../components/TabNav';
import ScrollReveal from '../components/ScrollReveal';
import { FIXED_H } from '../components/layoutConstants';
import { PLATFORM_META, displayUrl } from '../components/socialPlatforms';
import useApi from '../hooks/useApi';
import { publicApi } from '../api';
import { getVisitorId } from '../utils/analytics';

const tabDefs = [
  { key: 'form',    label: 'Formulaire',      eyebrow: 'Formulaire de contact', title: 'Nous Écrire'          },
  // { key: 'contact', label: 'Coordonnées',    eyebrow: 'Nous contacter',        title: 'Coordonnées'          },
  { key: 'social',  label: 'Réseaux sociaux',eyebrow: 'Retrouvez-nous',        title: 'Réseaux Sociaux'      },
];

const InfoRow = ({ href, icon: Icon, label, value, external }) => (
  <a
    href={href}
    target={external ? '_blank' : undefined}
    rel="noopener noreferrer"
    className="flex items-center gap-4 p-4 rounded-xl border border-stone-100 hover:border-brand-200 hover:bg-brand-50/30 transition-all group"
  >
    <div className="w-9 h-9 rounded-xl bg-stone-50 flex items-center justify-center text-stone-500 group-hover:bg-brand-800 group-hover:text-white transition-all shrink-0">
      <Icon size={14} />
    </div>
    <div>
      <p className="text-[10px] font-semibold uppercase tracking-widest text-stone-400 mb-0.5">{label}</p>
      <p className="text-sm font-medium text-stone-800">{value}</p>
    </div>
  </a>
);

const Toast = ({ message, type, onClose }) => (
  <motion.div
    initial={{ opacity: 0, y: 16 }}
    animate={{ opacity: 1, y: 0 }}
    exit={{ opacity: 0, y: 16 }}
    className="fixed bottom-6 left-1/2 -translate-x-1/2 bg-white rounded-2xl shadow-2xl border border-stone-100 px-6 py-4 z-50 flex items-center gap-4 min-w-[300px]"
  >
    {type === 'success'
      ? <FaCheckCircle className="text-green-500 shrink-0" size={18} />
      : <FaExclamationTriangle className="text-brand-800 shrink-0" size={18} />
    }
    <p className="text-sm font-medium text-stone-800">{message}</p>
    <button onClick={onClose} className="ml-auto text-xs text-stone-400 hover:text-stone-700">✕</button>
  </motion.div>
);

const ContactForm = () => {
  const [toast, setToast]     = useState(null);
  const [sending, setSending] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    const params = {
      nom: e.target.nom.value,
      prenom: e.target.prenom.value,
      email: e.target.email.value,
      telephone: e.target.telephone.value,
      message: e.target.message.value,
      visitor_id: getVisitorId(),
    };
    try {
      await publicApi.post('contact', params);
      setToast({ type: 'success', message: 'Message envoyé avec succès !' });
      e.target.reset();
    } catch {
      setToast({ type: 'error', message: 'Une erreur est survenue. Réessayez.' });
    } finally {
      setSending(false);
    }
  };
  return (
    <>
      <form onSubmit={handleSubmit} className="max-w-lg space-y-5">
        <div className="grid grid-cols-2 gap-4">
          <div><label className="block text-xs font-medium text-stone-600 mb-1.5">Nom</label>
            <input type="text" name="nom" className="input" placeholder="Dupont" required /></div>
          <div><label className="block text-xs font-medium text-stone-600 mb-1.5">Prénom</label>
            <input type="text" name="prenom" className="input" placeholder="Jean" required /></div>
        </div>
        <div><label className="block text-xs font-medium text-stone-600 mb-1.5">Email</label>
          <input type="email" name="email" className="input" placeholder="jean@email.fr" required /></div>
        <div><label className="block text-xs font-medium text-stone-600 mb-1.5">Téléphone</label>
          <input type="tel" name="telephone" className="input" placeholder="06 XX XX XX XX" required /></div>
        <div><label className="block text-xs font-medium text-stone-600 mb-1.5">Message</label>
          <textarea name="message" rows={4} className="input resize-none" placeholder="Votre message…" required /></div>
        <button type="submit" disabled={sending} className="btn-brand w-full">
          {sending ? 'Envoi…' : 'Envoyer le message'}
        </button>
      </form>
      <AnimatePresence>
        {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}
      </AnimatePresence>
    </>
  );
};

const Contact = ({ setCurrentSection, currentSection }) => {
  const { tab }     = useParams();
  const navigate    = useNavigate();
  const stickyRef   = useRef(null);
  const sectionRefs = useRef({});
  const activeRef   = useRef(null);
  const isProg      = useRef(false);
  const { data: socialLinks } = useApi('social_links');
  const [active, setActive] = useState(tab || 'form');

  const scrollOffset = useCallback(() => FIXED_H + (stickyRef.current?.offsetHeight ?? 0), []);

  const goTo = useCallback((key, behavior = 'smooth') => {
    const el = sectionRefs.current[key];
    if (!el) return;
    isProg.current = true;
    window.scrollTo({ top: el.getBoundingClientRect().top + window.scrollY - scrollOffset(), behavior });
    clearTimeout(goTo._t);
    goTo._t = setTimeout(() => { isProg.current = false; }, 900);
    setActive(key);
    navigate(`/contact/${key}`, { replace: true });
  }, [navigate, scrollOffset]);

  useEffect(() => {
    const key = tab || 'form';
    if (key !== 'form') { const t = setTimeout(() => goTo(key, 'instant'), 120); return () => clearTimeout(t); }
  }, []); // eslint-disable-line

  useEffect(() => {
    const onScroll = () => {
      if (isProg.current) return;
      const threshold = scrollOffset() + 40;
      let current = tabDefs[0].key;
      for (const { key } of tabDefs) {
        const el = sectionRefs.current[key];
        if (el && el.getBoundingClientRect().top <= threshold) current = key;
      }
      if (current !== activeRef.current) { activeRef.current = current; setActive(current); navigate(`/contact/${current}`, { replace: true }); }
    };
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener('scroll', onScroll);
  }, [navigate, scrollOffset]);

  const meta = tabDefs.find(t => t.key === active);

  return (
    <>
      <PageHeader title="Contact" subtitle="Nous sommes là pour répondre à toutes vos questions"
        setCurrentSection={setCurrentSection} currentSection={currentSection} />

      <div ref={stickyRef} className="sticky z-30 bg-white/95 backdrop-blur-md border-b border-stone-100 shadow-[0_1px_0_rgba(0,0,0,0.04)]" style={{ top: FIXED_H }}>
        <div className="max-w-6xl mx-auto px-5 md:pl-8 py-3 flex items-center gap-3 min-h-[52px]">
          <AnimatePresence mode="wait">
            <motion.div key={active} initial={{ opacity: 0, y: -8 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: 8 }}
              transition={{ duration: 0.25 }} className="flex items-center gap-3">
              <span className="text-[10px] font-semibold tracking-[0.2em] uppercase text-brand-800/60">{meta?.eyebrow}</span>
              <div className="w-px h-4 bg-stone-200 shrink-0" />
              <span className="text-sm font-bold text-stone-950">{meta?.title}</span>
            </motion.div>
          </AnimatePresence>
        </div>
        <div className="border-t border-stone-100">
          <div className="max-w-6xl mx-auto px-5 md:pl-8">
            <TabNav tabs={tabDefs} activeTab={active} onTabChange={goTo} />
          </div>
        </div>
      </div>

      {/* Section : Formulaire */}
      <div ref={el => { sectionRefs.current['form'] = el; }}>
        <section className="section bg-stone-50">
          <div className="max-w-6xl mx-auto">
            <ScrollReveal className="mb-8">
              <span className="eyebrow mb-3 block">Formulaire de contact</span>
              <h2 className="display-md">Nous Écrire</h2>
            </ScrollReveal>
            <ContactForm />
          </div>
        </section>
      </div>

      {/* Section : Coordonnées — masquée
      <div ref={el => { sectionRefs.current['contact'] = el; }}>
        <section className="section bg-white">
          <div className="max-w-6xl mx-auto">
            <ScrollReveal className="mb-8">
              <span className="eyebrow mb-3 block">Nous contacter</span>
              <h2 className="display-md">Coordonnées</h2>
            </ScrollReveal>
            <div className="max-w-md space-y-3">
              <InfoRow href="mailto:secretariatbceragny@gmail.com" icon={FaEnvelope} label="Email" value="secretariatbceragny@gmail.com" />
              <InfoRow href="tel:+33652757503" icon={FaPhone} label="Téléphone" value="+33 6 52 75 75 03" />
            </div>
          </div>
        </section>
      </div>
      */}

      {/* Section : Réseaux sociaux */}
      <div ref={el => { sectionRefs.current['social'] = el; }}>
        <section className="section bg-stone-50">
          <div className="max-w-6xl mx-auto">
            <ScrollReveal className="mb-8">
              <span className="eyebrow mb-3 block">Retrouvez-nous</span>
              <h2 className="display-md">Réseaux Sociaux</h2>
            </ScrollReveal>
            <div className="max-w-md space-y-3">
              {socialLinks.filter(s => PLATFORM_META[s.platform]).map(s => (
                <InfoRow
                  key={s.id}
                  href={s.url}
                  icon={PLATFORM_META[s.platform].icon}
                  label={PLATFORM_META[s.platform].label}
                  value={displayUrl(s.url)}
                  external
                />
              ))}
              {socialLinks.length === 0 && (
                <p className="text-sm text-stone-400">Aucun réseau social pour le moment.</p>
              )}
            </div>
          </div>
        </section>
      </div>
    </>
  );
};

export default Contact;
